/**
 * What common CS2 console commands do, in plain words, for the readable view of a
 * config. `cheat`: needs sv_cheats 1 (a private server). `server`: only does
 * something on a server you host (practice, LAN).
 */
export interface CfgCommandInfo {
  explain: string;
  cheat?: boolean;
  server?: boolean;
}

const c = (explain: string, extra: Omit<CfgCommandInfo, "explain"> = {}): CfgCommandInfo => ({ explain, ...extra });
const cheat = (explain: string) => c(explain, { cheat: true, server: true });
const server = (explain: string) => c(explain, { server: true });

export const CFG_COMMANDS: Record<string, CfgCommandInfo> = {
  // Binds and scripting
  bind: c("Binds a key to a command."),
  unbind: c("Removes what a key does."),
  unbindall: c("Removes every key bind, yours included."),
  alias: c("Defines a new command name that runs other commands."),
  exec: c("Runs another config file."),
  echo: c("Prints text in the console."),
  toggle: c("Switches a setting between values each time it runs."),
  incrementvar: c("Steps a setting up or down each time it runs."),
  say: c("Writes in all chat."),
  say_team: c("Writes in team chat."),
  clear: c("Clears the console."),
  host_writeconfig: c("Saves your current settings to config files."),
  // Crosshair
  apply_crosshair_code: c("Sets your whole crosshair from a share code."),
  cl_crosshairstyle: c("Crosshair style (0 default, 1 default static, 2 classic, 3 classic dynamic, 4 classic static, 5 legacy)."),
  cl_crosshairsize: c("Length of the crosshair lines."),
  cl_crosshairthickness: c("Thickness of the crosshair lines."),
  cl_crosshairgap: c("Space in the middle of the crosshair."),
  cl_crosshaircolor: c("Crosshair colour preset (0 red, 1 green, 2 yellow, 3 blue, 4 cyan, 5 custom)."),
  cl_crosshaircolor_r: c("Custom crosshair colour: red part (0–255)."),
  cl_crosshaircolor_g: c("Custom crosshair colour: green part (0–255)."),
  cl_crosshaircolor_b: c("Custom crosshair colour: blue part (0–255)."),
  cl_crosshairalpha: c("Crosshair opacity (0–255)."),
  cl_crosshairusealpha: c("Uses the opacity setting for the crosshair."),
  cl_crosshairdot: c("Shows a dot in the middle of the crosshair."),
  cl_crosshair_drawoutline: c("Draws a black outline around the crosshair."),
  cl_crosshair_outlinethickness: c("Thickness of the crosshair outline."),
  cl_crosshair_t: c("T-shaped crosshair (no top line)."),
  cl_crosshairgap_useweaponvalue: c("Lets the weapon change the crosshair gap."),
  cl_crosshair_recoil: c("Moves the crosshair with recoil."),
  cl_crosshair_dynamic_splitdist: c("How far the dynamic crosshair splits."),
  cl_crosshair_dynamic_splitalpha_innermod: c("Opacity of the inner part of a split crosshair."),
  cl_crosshair_dynamic_splitalpha_outermod: c("Opacity of the outer part of a split crosshair."),
  cl_crosshair_dynamic_maxdist_splitratio: c("Size ratio of the split crosshair parts."),
  cl_crosshair_sniper_width: c("Thickness of the sniper scope lines."),
  cl_fixedcrosshairgap: c("Gap of the fixed crosshair styles."),
  // Viewmodel and hands
  viewmodel_fov: c("Field of view of your weapon (how big it looks, 54–68)."),
  viewmodel_offset_x: c("Moves the weapon left or right."),
  viewmodel_offset_y: c("Moves the weapon forward or back."),
  viewmodel_offset_z: c("Moves the weapon up or down."),
  viewmodel_presetpos: c("Weapon position preset (1 desktop, 2 couch, 3 classic)."),
  cl_prefer_lefthanded: c("Holds the weapon in the left hand."),
  cl_righthand: c("Holds the weapon in the right hand (older games)."),
  cl_bob_lower_amt: c("How far the weapon moves down when you run."),
  cl_bobamt_lat: c("Side-to-side weapon sway when you run."),
  cl_bobamt_vert: c("Up-and-down weapon sway when you run."),
  cl_bobcycle: c("Speed of the weapon sway when you run."),
  cl_viewmodel_shift_left_amt: c("How far the weapon shifts left when you spray."),
  cl_viewmodel_shift_right_amt: c("How far the weapon shifts right when you spray."),
  // Mouse
  sensitivity: c("Mouse sensitivity."),
  zoom_sensitivity_ratio: c("Mouse sensitivity when scoped, compared with normal."),
  m_yaw: c("How much horizontal mouse movement turns you."),
  m_pitch: c("How much vertical mouse movement turns you (negative inverts)."),
  m_rawinput: c("Reads the mouse directly, without Windows acceleration."),
  m_customaccel: c("Mouse acceleration."),
  // Radar
  cl_radar_scale: c("Radar zoom: smaller shows more of the map."),
  cl_radar_always_centered: c("Keeps you in the middle of the radar."),
  cl_radar_rotate: c("Turns the radar with you."),
  cl_radar_icon_scale_min: c("Minimum size of the player dots on the radar."),
  cl_radar_square_with_scoreboard: c("Shows the whole square radar when the scoreboard is open."),
  cl_hud_radar_scale: c("Size of the radar on screen."),
  // HUD
  cl_hud_color: c("Colour of the HUD."),
  cl_hud_background_alpha: c("Opacity of the HUD background."),
  cl_hud_healthammo_style: c("Style of the health and ammo display."),
  cl_hud_playercount_pos: c("Where the player count sits (top or bottom)."),
  cl_hud_playercount_showcount: c("Shows team counts instead of player icons."),
  hud_scaling: c("Size of the HUD."),
  hud_showtargetid: c("Shows the name of the player you aim at."),
  safezonex: c("Width of the area the HUD uses."),
  safezoney: c("Height of the area the HUD uses."),
  cl_showloadout: c("Always shows your weapons on the HUD."),
  cl_showpos: c("Shows your position and speed on screen."),
  cl_showfps: c("Shows frames per second on screen."),
  cl_drawhud: c("Shows or hides the whole HUD."),
  cl_draw_only_deathnotices: c("Hides the HUD except the kill feed (for recording)."),
  cl_teammate_colors_show: c("Shows teammate colours on the radar and HUD."),
  cl_teamid_overhead_mode: c("What is shown above teammates' heads."),
  cl_teamid_overhead_maxdist: c("How far away teammates' names show above their heads."),
  cl_color: c("Your preferred player colour in matchmaking."),
  gameinstructor_enable: c("Shows the in-game tips."),
  cl_autohelp: c("Shows help messages."),
  cl_showhelp: c("Shows help messages."),
  // Gameplay helpers
  cl_autowepswitch: c("Switches to a weapon you pick up."),
  cl_use_opens_buy_menu: c("Opens the buy menu with the use key in the buy zone."),
  cl_sniper_delay_unscope: c("Waits before unscoping after a shot."),
  cl_silencer_mode: c("Silencer on or off by default."),
  cl_dm_buyrandomweapons: c("Random weapons in deathmatch."),
  buy: c("Buys an item."),
  // Network and performance
  rate: c("Most data per second the game may receive."),
  cl_interp: c("Network interpolation (older games; CS2 manages it)."),
  cl_interp_ratio: c("Network interpolation ratio (older games)."),
  cl_updaterate: c("Updates per second from the server (older games)."),
  cl_cmdrate: c("Commands per second to the server (older games)."),
  fps_max: c("Highest frames per second (0 for no limit)."),
  fps_max_ui: c("Highest frames per second in menus."),
  fps_max_tools: c("Highest frames per second in the tools."),
  mat_monitorgamma: c("Screen brightness."),
  r_fullscreen_gamma: c("Screen brightness in fullscreen."),
  r_drawtracers_firstperson: c("Shows bullet tracers for your own shots."),
  cl_net_buffer_ticks: c("Network buffer (smoothness against loss)."),
  cq_netgraph: c("Shows the network graph."),
  // Sound and voice
  volume: c("Master volume."),
  snd_musicvolume: c("Music volume."),
  snd_menumusic_volume: c("Menu music volume."),
  snd_roundstart_volume: c("Round start music volume."),
  snd_roundend_volume: c("Round end music volume."),
  snd_roundaction_volume: c("Round action music volume."),
  snd_mapobjective_volume: c("Bomb and hostage music volume."),
  snd_tensecondwarning_volume: c("Ten-second warning volume."),
  snd_mvp_volume: c("MVP music volume."),
  snd_deathcamera_volume: c("Death camera music volume."),
  snd_mute_losefocus: c("Mutes the game when it is in the background."),
  snd_headphone_eq: c("Headphone equaliser."),
  snd_spatialize_lerp: c("Stereo separation of sounds."),
  voice_enable: c("Turns voice chat on or off."),
  voice_scale: c("Voice chat volume."),
  voice_modenable: c("Allows voice chat."),
  "+voicerecord": c("Talks while held."),
  // Actions
  "+attack": c("Shoots while held."),
  "+attack2": c("Secondary fire while held."),
  "+jump": c("Jumps."),
  "+duck": c("Crouches while held."),
  "+sprint": c("Walks quietly while held."),
  "+speed": c("Walks quietly while held."),
  "+forward": c("Moves forward while held."),
  "+back": c("Moves back while held."),
  "+left": c("Turns left while held."),
  "+right": c("Turns right while held."),
  "+moveleft": c("Strafes left while held."),
  "+moveright": c("Strafes right while held."),
  "+use": c("Uses (defuse, open doors) while held."),
  "+reload": c("Reloads."),
  "+lookatweapon": c("Inspects the weapon."),
  "+showscores": c("Shows the scoreboard while held."),
  "+cl_show_team_equipment": c("Shows teammates' equipment while held."),
  drop: c("Drops your weapon."),
  slot1: c("Primary weapon."),
  slot2: c("Pistol."),
  slot3: c("Knife."),
  slot4: c("Grenades (cycles)."),
  slot5: c("Bomb."),
  slot6: c("HE grenade."),
  slot7: c("Flashbang."),
  slot8: c("Smoke grenade."),
  slot9: c("Decoy."),
  slot10: c("Molotov or incendiary."),
  slot11: c("Zeus."),
  slot12: c("Healthshot."),
  lastinv: c("Switches to your last weapon."),
  invprev: c("Previous weapon."),
  invnext: c("Next weapon."),
  use: c("Switches to the named weapon."),
  player_ping: c("Pings the spot you aim at."),
  radio: c("Opens the radio menu."),
  getpos: c("Prints your position and view as a setpos line."),
  getpos_exact: c("Prints your exact position and view."),
  // Practice server
  sv_cheats: server("Allows cheat-protected commands on this server (practice)."),
  setpos: cheat("Moves you to a position."),
  setang: cheat("Sets your view angles."),
  setpos_exact: cheat("Moves you to an exact position."),
  noclip: cheat("Flies through walls."),
  god: cheat("Makes you invulnerable."),
  give: cheat("Gives you an item."),
  impulse: cheat("Runs a special action (101: all weapons and money)."),
  sv_infinite_ammo: cheat("Infinite ammo (1) or infinite reserve (2)."),
  sv_grenade_trajectory_prac_pipreview: cheat("Shows where a grenade will go before you throw it."),
  sv_grenade_trajectory_prac_trailtime: cheat("How long the grenade path stays on screen."),
  sv_grenade_trajectory: cheat("Draws the path of thrown grenades."),
  sv_grenade_trajectory_time: cheat("How long the grenade path stays on screen."),
  sv_showimpacts: cheat("Shows where bullets hit."),
  sv_showimpacts_time: cheat("How long bullet impacts stay on screen."),
  sv_rethrow_last_grenade: cheat("Throws your last grenade again from the same spot."),
  ammo_grenade_limit_total: server("How many grenades you can carry."),
  sv_regeneration_force_on: cheat("Health regenerates."),
  mp_warmup_end: server("Ends the warm-up."),
  mp_warmup_start: server("Starts the warm-up."),
  mp_warmup_pausetimer: server("Pauses the warm-up timer."),
  mp_restartgame: server("Restarts the match after the given seconds."),
  mp_roundtime: server("Round length in minutes."),
  mp_roundtime_defuse: server("Round length on bomb maps, in minutes."),
  mp_roundtime_hostage: server("Round length on hostage maps, in minutes."),
  mp_freezetime: server("Freeze time at round start, in seconds."),
  mp_buytime: server("How long you can buy, in seconds."),
  mp_buy_anywhere: server("Buy anywhere on the map."),
  mp_maxmoney: server("Most money a player can have."),
  mp_startmoney: server("Money at the start."),
  mp_afterroundmoney: server("Money given after each round."),
  mp_limitteams: server("How uneven teams can be."),
  mp_autoteambalance: server("Balances teams automatically."),
  mp_autokick: server("Kicks idle or team-killing players."),
  mp_respawn_on_death_t: server("Terrorists respawn after dying."),
  mp_respawn_on_death_ct: server("Counter-Terrorists respawn after dying."),
  mp_drop_knife_enable: server("Lets players drop their knife."),
  mp_ignore_round_win_conditions: server("Rounds do not end."),
  mp_c4timer: server("Bomb timer, in seconds."),
  mp_death_drop_gun: server("Players drop their gun when they die."),
  mp_solid_teammates: server("Teammates block each other."),
  mp_spectators_max: server("Most spectators."),
  mp_friendlyfire: server("Friendly fire."),
  mp_maxrounds: server("Most rounds in a match."),
  mp_overtime_enable: server("Overtime when tied."),
  mp_pause_match: server("Pauses the match at the next freeze time."),
  mp_unpause_match: server("Ends the pause."),
  mp_teamname_1: server("Name of team 1."),
  mp_teamname_2: server("Name of team 2."),
  bot_kick: server("Removes all bots."),
  bot_add: server("Adds a bot."),
  bot_add_t: server("Adds a Terrorist bot."),
  bot_add_ct: server("Adds a Counter-Terrorist bot."),
  bot_place: cheat("Puts a bot where you aim."),
  bot_stop: cheat("Bots stand still."),
  bot_freeze: cheat("Bots do not move."),
  bot_dont_shoot: cheat("Bots do not shoot."),
  bot_mimic: cheat("Bots copy your movement."),
  bot_crouch: cheat("Bots crouch."),
  bot_quota: server("How many bots."),
  bot_difficulty: server("Bot difficulty."),
  map: server("Loads a map."),
  changelevel: server("Changes the map."),
  host_timescale: cheat("Game speed."),
  ent_fire: cheat("Sends an order to things on the map (ent_fire smokegrenade_projectile kill removes the smokes in the air or on the ground)."),
  stopsound: c("Stops the sounds playing now."),
  mp_team_intro_time: server("Length of the team intro before the first round, in seconds (0: none)."),
  cl_versus_intro: c("Shows the versus intro before the match."),
  // Connection
  connect: c("Joins a server."),
  disconnect: c("Leaves the server."),
  retry: c("Joins the last server again."),
  quit: c("Closes the game."),
  status: c("Lists the players on the server."),
  // Demos and spectating
  demo_timescale: c("Demo playback speed."),
  demoui: c("Opens the demo player."),
  playdemo: c("Plays a demo."),
  record: c("Records a demo."),
  stop: c("Stops recording a demo."),
  spec_show_xray: c("Shows players through walls when spectating."),
  tv_listen_voice_indices: c("Hears voice chat in GOTV."),
};

/** Families of settings, for commands the dictionary does not list one by one. */
export const CFG_FAMILIES: { prefix: string; explain: string; server?: boolean }[] = [
  { prefix: "cl_crosshair", explain: "A crosshair setting." },
  { prefix: "viewmodel_", explain: "A weapon view setting." },
  { prefix: "cl_radar", explain: "A radar setting." },
  { prefix: "cl_hud", explain: "A HUD setting." },
  { prefix: "snd_", explain: "A sound setting." },
  { prefix: "voice_", explain: "A voice chat setting." },
  { prefix: "m_", explain: "A mouse setting." },
  { prefix: "r_", explain: "A rendering setting." },
  { prefix: "mat_", explain: "A rendering setting." },
  { prefix: "mp_", explain: "A match setting: only on a server you host.", server: true },
  { prefix: "bot_", explain: "A bot setting: only on a server you host.", server: true },
  { prefix: "sv_", explain: "A server setting: only on a server you host.", server: true },
];
